import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSupabase } from '@/lib/supabase';
import { useAuthReady } from '@/hooks/useAuthReady';

// Supported entity types for soft delete
export type SoftDeleteEntityType =
  | 'bookings'
  | 'properties'
  | 'units'
  | 'payments'
  | 'booking_documents'
  | 'gallery_images'
  | 'local_recommendations'
  | 'landlord_services'
  | 'unit_facilities'
  | 'seasonal_prices'
  | 'payment_methods'
  | 'custom_amenities'
  | 'team_members'
  | 'booking_groups';

// RPC response shape — matches PART 3 of the SQL migration
export interface SoftDeleteResult {
  ok: boolean;
  error_code?:
    | 'NOT_FOUND'
    | 'FORBIDDEN'
    | 'HAS_ACTIVE_BOOKINGS'
    | 'INVALID_ENTITY_TYPE'
    | 'NOT_IN_TRASH'
    | 'PARENT_DELETED'
    // Restore would exceed the plan (trashed items no longer count) — the
    // backend sends a Croatian error_message explaining what to do.
    | 'LIMIT_REACHED'
    // Restoring a booking whose dates another booking took meanwhile.
    | 'DATES_TAKEN';
  error_message?: string;
  already_deleted?: boolean;
  not_deleted?: boolean;
  not_in_trash?: boolean;
  cascade_units?: number;
  cascade_payments?: number;
  restored_units?: number;
}

export interface TrashItem {
  entity_type: SoftDeleteEntityType;
  entity_id: string;
  display_name: string;
  deleted_at: string;
  days_until_purge: number;
  metadata: Record<string, unknown>;
}

export const trashKeys = {
  all: ['trash'] as const,
  items: () => [...trashKeys.all, 'items'] as const,
  count: () => [...trashKeys.all, 'count'] as const,
};

// ============================================================
// SOFT DELETE — dispatches to the right RPC per entity type
// ============================================================
async function callSoftDelete(
  entityType: SoftDeleteEntityType,
  entityId: string
): Promise<SoftDeleteResult> {
  const supabase = getSupabase();

  let rpcName: string;
  let params: Record<string, unknown>;

  if (entityType === 'bookings') {
    rpcName = 'soft_delete_booking';
    params = { p_booking_id: entityId };
  } else if (entityType === 'properties') {
    rpcName = 'soft_delete_property';
    params = { p_property_id: entityId };
  } else if (entityType === 'units') {
    rpcName = 'soft_delete_unit';
    params = { p_unit_id: entityId };
  } else {
    rpcName = 'soft_delete_entity';
    params = { p_entity_type: entityType, p_entity_id: entityId };
  }

  const { data, error } = await supabase.rpc(rpcName, params);
  if (error) throw error;
  return data as SoftDeleteResult;
}

async function callRestore(
  entityType: SoftDeleteEntityType,
  entityId: string
): Promise<SoftDeleteResult> {
  const supabase = getSupabase();

  let rpcName: string;
  let params: Record<string, unknown>;

  if (entityType === 'bookings') {
    rpcName = 'restore_booking';
    params = { p_booking_id: entityId };
  } else if (entityType === 'properties') {
    rpcName = 'restore_property';
    params = { p_property_id: entityId };
  } else if (entityType === 'units') {
    rpcName = 'restore_unit';
    params = { p_unit_id: entityId };
  } else {
    rpcName = 'restore_entity';
    params = { p_entity_type: entityType, p_entity_id: entityId };
  }

  const { data, error } = await supabase.rpc(rpcName, params);
  if (error) throw error;
  return data as SoftDeleteResult;
}

async function callPermanentDelete(
  entityType: SoftDeleteEntityType,
  entityId: string
): Promise<SoftDeleteResult> {
  const supabase = getSupabase();
  const { data, error } = await supabase.rpc('permanent_delete_entity', {
    p_entity_type: entityType,
    p_entity_id: entityId,
  });
  if (error) throw error;
  return data as SoftDeleteResult;
}

// ============================================================
// HOOKS
// ============================================================

/**
 * Soft delete an entity. Moves it to trash for 30 days.
 * Returns the mutation; mutate({ entityType, entityId }).
 */
export function useSoftDelete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
    }: {
      entityType: SoftDeleteEntityType;
      entityId: string;
    }) => callSoftDelete(entityType, entityId),
    onSuccess: (result, variables) => {
      // Invalidate the relevant list queries
      invalidateForEntity(queryClient, variables.entityType);
      // Always refresh the trash queries
      queryClient.invalidateQueries({ queryKey: trashKeys.all });

      // Surface the result so callers can inspect ok/error_code
      return result;
    },
  });
}

/**
 * Restore an entity from trash.
 */
export function useRestore() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
    }: {
      entityType: SoftDeleteEntityType;
      entityId: string;
    }) => callRestore(entityType, entityId),
    onSuccess: (result, variables) => {
      // Only invalidate caches if the restore actually succeeded.
      // Backend can refuse (PARENT_DELETED, FORBIDDEN) — in that case the
      // row is still in trash, so trash list shouldn't be refetched as if
      // something changed.
      if (result.ok) {
        invalidateForEntity(queryClient, variables.entityType);
        queryClient.invalidateQueries({ queryKey: trashKeys.all });
      }
    },
  });
}

/**
 * Permanently delete an entity from trash. Cannot be undone.
 */
export function usePermanentDelete() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      entityType,
      entityId,
    }: {
      entityType: SoftDeleteEntityType;
      entityId: string;
    }) => callPermanentDelete(entityType, entityId),
    onSuccess: (_result, variables) => {
      invalidateForEntity(queryClient, variables.entityType);
      queryClient.invalidateQueries({ queryKey: trashKeys.all });
    },
  });
}

/**
 * Fetch all soft-deleted items the current user owns.
 */
export function useTrashItems() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: trashKeys.items(),
    queryFn: async (): Promise<TrashItem[]> => {
      if (!userId) return [];
      const { data, error } = await getSupabase().rpc('get_trash_items');
      if (error) throw error;
      return (data ?? []) as TrashItem[];
    },
    enabled: isReady,
  });
}

/**
 * Count of soft-deleted items (for sidebar badge).
 */
export function useTrashCount() {
  const { userId, isReady } = useAuthReady();

  return useQuery({
    queryKey: trashKeys.count(),
    queryFn: async (): Promise<number> => {
      if (!userId) return 0;
      const { data, error } = await getSupabase().rpc('get_trash_count');
      if (error) throw error;
      return (data as number) ?? 0;
    },
    enabled: isReady,
    // Refresh every minute so the badge stays current
    refetchInterval: 60_000,
  });
}

// ============================================================
// Helpers
// ============================================================
function invalidateForEntity(
  queryClient: ReturnType<typeof useQueryClient>,
  entityType: SoftDeleteEntityType
) {
  // Map entity type → query key prefix used by its hook
  const map: Record<SoftDeleteEntityType, string[]> = {
    // 'calendar' / 'seasonalOccupancy': the calendars and occupancy card read these too
    bookings: ['bookings', 'calendar', 'seasonalOccupancy'],
    properties: ['properties', 'calendar', 'seasonalOccupancy'],
    units: ['units', 'calendar', 'seasonalOccupancy'],
    payments: ['payments'],
    booking_documents: ['booking-documents', 'documents'],
    gallery_images: ['gallery-images', 'gallery'],
    local_recommendations: ['local-recommendations', 'recommendations'],
    landlord_services: ['landlord-services', 'services'],
    unit_facilities: ['unit-facilities', 'facilities'],
    seasonal_prices: ['seasonalPrices', 'calendar', 'seasonalOccupancy'],
    payment_methods: ['payment-methods'],
    custom_amenities: ['custom-amenities'],
    team_members: ['team-members', 'team'],
    booking_groups: ['booking-groups'],
  };

  for (const key of map[entityType]) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
  // Units / properties count against the plan: its limits and billing row too.
  if (entityType === 'units' || entityType === 'properties') {
    queryClient.invalidateQueries({ queryKey: ['subscription'] });
  }
}

/**
 * Human-readable error message from RPC result.
 */
export function getSoftDeleteErrorMessage(
  result: SoftDeleteResult,
  t: (key: string) => string
): string | null {
  if (result.ok) return null;
  if (result.error_message) return result.error_message;
  if (result.error_code === 'HAS_ACTIVE_BOOKINGS') {
    return t('softDelete.error.hasActiveBookings');
  }
  if (result.error_code === 'FORBIDDEN') {
    return t('softDelete.error.forbidden');
  }
  if (result.error_code === 'NOT_FOUND') {
    return t('softDelete.error.notFound');
  }
  if (result.error_code === 'NOT_IN_TRASH') {
    return t('softDelete.error.notInTrash');
  }
  if (result.error_code === 'PARENT_DELETED') {
    return t('softDelete.error.parentDeleted');
  }
  if (result.error_code === 'DATES_TAKEN') {
    return t('softDelete.error.datesTaken');
  }
  return t('softDelete.error.generic');
}
